import { Switch as SwitchPrimitive } from '@base-ui/react/switch';
import type { ReactNode } from 'react';
import { cn } from '../utils/cn';

type SwitchProps = {
  checked?: boolean;
  defaultChecked?: boolean;
  disabled?: boolean;
  loading?: boolean;
  isLoading?: boolean;
  size?: 'default' | 'small';
  checkedChildren?: ReactNode;
  unCheckedChildren?: ReactNode;
  name?: string;
  value?: string;
  className?: string;
  thumbClassName?: string;
  onChange?: (checked: boolean) => void;
  onCheckedChange?: (checked: boolean) => void;
};

function Switch({
  checked,
  defaultChecked,
  disabled = false,
  loading = false,
  isLoading = false,
  size = 'default',
  checkedChildren,
  unCheckedChildren,
  name,
  value,
  className,
  thumbClassName,
  onChange,
  onCheckedChange,
}: SwitchProps) {
  const isBusy = loading || isLoading;
  const isSmall = size === 'small';

  return (
    <SwitchPrimitive.Root
      checked={checked}
      defaultChecked={defaultChecked}
      disabled={disabled || isBusy}
      name={name}
      value={value}
      onCheckedChange={nextChecked => {
        onCheckedChange?.(nextChecked);
        onChange?.(nextChecked);
      }}
      data-slot="switch"
      className={cn(
        'relative inline-flex shrink-0 items-center rounded-full bg-muted transition-colors data-checked:bg-foreground data-disabled:opacity-60',
        isSmall ? 'h-5 w-9' : 'h-6 w-11',
        isBusy ? 'cursor-progress' : 'cursor-pointer data-disabled:cursor-not-allowed',
        className,
      )}
    >
      {checkedChildren || unCheckedChildren ? (
        <span
          className={cn(
            'pointer-events-none absolute inset-0 flex items-center text-[10px] font-medium leading-none text-background/90',
            isSmall ? 'px-1' : 'px-1.5',
            checked ? 'justify-start' : 'justify-end',
          )}
        >
          {checked ? checkedChildren : unCheckedChildren}
        </span>
      ) : null}
      <SwitchPrimitive.Thumb
        className={cn(
          'absolute left-0.5 rounded-full bg-background shadow-xs transition-transform',
          isSmall
            ? 'top-0.5 h-4 w-4 data-checked:translate-x-4'
            : 'top-0.5 h-5 w-5 data-checked:translate-x-5',
          thumbClassName,
        )}
      />
    </SwitchPrimitive.Root>
  );
}

export { Switch };
export type { SwitchProps };
